import { Engine as BabylonEngine } from "@babylonjs/core/Engines/engine";
import { SceneFootstepAudio } from "../audio/SceneFootstepAudio";
import type { Direction } from "../core/types";
import { FramePerformanceMonitor } from "../diagnostics/FramePerformanceMonitor";
import { DesktopInput } from "../input/DesktopInput";
import { QuestXRInput, type XRExitReason } from "../input/QuestXRInput";
import { WhiteboxMaze } from "../scene/WhiteboxMaze";
import { SceneTrainingEngine } from "../scene-training/SceneTrainingEngine";
import {
  createSceneEvent,
  exportSceneEvents,
  SceneSessionStore,
  type SceneEvent,
  type SceneSessionRecord,
} from "../scene-training/SceneSessionStore";
import { SceneStimulusCatalogRepository } from "../stimuli/SceneStimulusCatalogRepository";
import { SceneTrainingHud, type SceneStartRequest } from "../ui/SceneTrainingHud";

const FOCUS_DWELL_MS = 150;
const BEHAVIOR_EVENT_TYPES = new Set([
  "session_start",
  "session_complete",
  "scene_decision_generated",
  "direction_focused",
  "answer_selected",
  "answer_committed",
  "reveal_started",
  "scene_complete",
  "scene_failed",
]);

export class SceneTrainingApp {
  private readonly hud: SceneTrainingHud;
  private readonly babylon: BabylonEngine;
  private readonly maze: WhiteboxMaze;
  private readonly audio = new SceneFootstepAudio();
  private readonly store = new SceneSessionStore();
  private readonly catalogRepository = new SceneStimulusCatalogRepository();
  private readonly performanceMonitor = new FramePerformanceMonitor();
  private readonly input: DesktopInput;
  private readonly xr: QuestXRInput;
  private engine: SceneTrainingEngine | null = null;
  private record: SceneSessionRecord | null = null;
  private xrSupported = false;
  private focusedDirection: Direction | null = null;
  private focusCandidate: Direction | null = null;
  private focusCandidateSince = 0;
  private pendingDirection: Direction | null = null;
  private moving = false;
  private revealReady = false;
  private accumulatedMs = 0;
  private activeStartedAt: number | null = null;
  private sceneStartedElapsedMs = 0;
  private decisionStartedElapsedMs = 0;
  private selectedElapsedMs: number | null = null;
  private lastTimerUpdate = 0;
  private saveQueue = Promise.resolve();
  private visibilitySuspended = false;

  constructor(root: HTMLElement) {
    this.hud = new SceneTrainingHud(root);
    this.babylon = new BabylonEngine(this.hud.canvas, true, { stencil: true, preserveDrawingBuffer: false });
    this.maze = new WhiteboxMaze(this.hud.canvas, this.babylon);
    this.xr = new QuestXRInput(this.maze.scene, {
      onPreviewOrCancel: () => this.previewOrCancelSelection(),
      onPrimary: () => this.primaryAction(),
      onContinue: () => this.continueToNextDecision(),
      onCancel: () => this.cancelSelection(),
      onPause: () => this.togglePause(),
      onMovementChanged: (moving) => this.onMovementChanged(moving),
      onMoveRequested: (displacement) => this.maze.moveParticipant(displacement),
      onSessionStarted: () => this.log("xr_enter", { referenceSpace: "local-floor", speedMetersPerSecond: 1.2 }),
      onSessionEnded: (reason) => this.onXRSessionEnded(reason),
    });
    this.input = new DesktopInput(this.hud.canvas, this.maze.camera, {
      onPreview: () => this.previewAudio(),
      onPrimary: () => this.primaryAction(),
      onContinue: () => this.continueToNextDecision(),
      onCancel: () => this.cancelSelection(),
      onPause: () => this.togglePause(),
      onMovementChanged: (moving) => this.onMovementChanged(moving),
      onMoveRequested: (displacement) => this.maze.moveParticipant(displacement),
    });
    this.hud.setActions(() => this.previewAudio(), () => this.primaryAction(), () => this.continueToNextDecision(), () => this.cancelSelection());
    this.babylon.runRenderLoop(() => this.renderFrame());
    window.addEventListener("resize", () => this.babylon.resize());
    window.addEventListener("pagehide", () => { this.suspendClock(); void this.save(); });
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this.visibilitySuspended = true;
        this.suspendClock();
        void this.save();
      } else if (this.visibilitySuspended) {
        this.visibilitySuspended = false;
        this.resumeClockIfNeeded();
      }
    });
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      const [catalog] = await Promise.all([
        this.catalogRepository.load(),
        this.store.open(),
        this.xr.initialize().then((supported) => { this.xrSupported = supported; }).catch(() => { this.xrSupported = false; }),
      ]);
      if (catalog.scenes.filter((scene) => scene.enabled).length !== 4) throw new Error("启用场景必须正好为 4 个");
      const preload = catalog.scenes.find((scene) => scene.enabled)?.audio[0];
      if (preload) this.audio.setStimulus(preload);
      const active = (await this.store.list()).find((item) => item.status === "active");
      if (active) {
        this.hud.showRecovery(active, () => void this.resumeRecord(active), () => void this.abandon(active), () => void this.download(active));
      } else this.showPreflight();
    } catch (error) {
      this.hud.toastMessage(`初始化失败：${message(error)}`, "wrong");
      this.showPreflight();
    }
  }

  private showPreflight(): void {
    this.maze.showInstructionRoom(true);
    this.hud.showPreflight(this.xrSupported, (request) => void this.startSession(request), () => void this.showRecords());
  }

  private async startSession(request: SceneStartRequest): Promise<void> {
    this.audio.unlock();
    if (await this.store.get(request.sessionId)) return void this.hud.toastMessage("会话编号已存在。", "wrong");
    const catalog = await this.catalogRepository.load();
    const seed = createSeed();
    this.engine = new SceneTrainingEngine(catalog.scenes.filter((scene) => scene.enabled), seed, request.mode);
    this.engine.startInstruction();
    const now = new Date().toISOString();
    this.accumulatedMs = 0;
    this.activeStartedAt = null;
    this.record = {
      schemaVersion: 1,
      participantId: request.participantId,
      sessionId: request.sessionId,
      catalogVersion: catalog.catalogVersion,
      status: "active",
      createdAt: now,
      updatedAt: now,
      engine: this.engine.toSnapshot(),
      events: [],
      activeElapsedMs: 0,
      sceneStartedElapsedMs: 0,
      activeClockStartedAt: null,
    };
    this.log("session_start", {});
    this.showInstruction();
  }

  private showInstruction(): void {
    const engine = this.requireEngine();
    const page = engine.getInstructionPage();
    this.maze.showInstructionRoom(true);
    this.hud.showInstruction(
      page,
      this.xrSupported,
      () => { if (page > 0) engine.setInstructionPage(page - 1); void this.save(); this.showInstruction(); },
      () => { if (page < 2) engine.setInstructionPage(page + 1); void this.save(); this.showInstruction(); },
      () => void this.finishInstruction(false),
      () => void this.finishInstruction(true),
    );
  }

  private async finishInstruction(skipPractice: boolean): Promise<void> {
    const engine = this.requireEngine();
    engine.completeInstruction(skipPractice);
    this.log(skipPractice ? "practice_skipped" : "practice_started", {});
    this.maze.showInstructionRoom(false);
    await this.enterXR();
    if (skipPractice) {
      this.startClock();
      this.sceneStartedElapsedMs = this.currentElapsedMs();
    }
    this.startDecision(!skipPractice);
  }

  private startDecision(practice = false): void {
    const engine = this.requireEngine();
    const decision = engine.getCurrentDecision();
    if (!decision) throw new Error("场景判断不存在");
    const candidates = practice ? decision.candidates : engine.getVisibleCandidates();
    this.maze.showInstructionRoom(false);
    this.maze.configureSceneDecision(decision.directionColors, candidates);
    this.maze.resetParticipant();
    this.audio.setStimulus(decision.audio);
    if (this.moving && (engine.getPhase() === "practice_active" || engine.getPhase() === "scene_active")) this.audio.setMoving(true);
    this.focusedDirection = null;
    this.focusCandidate = null;
    this.pendingDirection = null;
    this.decisionStartedElapsedMs = this.currentElapsedMs();
    this.selectedElapsedMs = null;
    this.revealReady = false;
    this.hud.hideModal();
    this.hud.updateDecision(engine.getSceneIndex(), engine.getSceneCount(), engine.getDecisionIndex(), engine.getDecisionCount(), practice);
    this.hud.setFocus(null, null);
    this.log(practice ? "practice_trial_generated" : "scene_decision_generated", {
      targetSceneId: decision.targetScene.id,
      audioId: decision.audio.id,
      candidates: candidates.map((candidate) => ({ direction: candidate.direction, sceneId: candidate.sceneId, photoId: candidate.photo.id })),
      directionColors: decision.directionColors,
    });
  }

  private renderFrame(): void {
    const deltaMs = this.babylon.getDeltaTime();
    const deltaSeconds = Math.min(deltaMs / 1000, 0.05);
    const phase = this.engine?.getPhase();
    const enabled = phase === "practice_active" || phase === "scene_active" || phase === "reveal";
    this.input.setEnabled(Boolean(enabled && !this.xr.isInXR));
    this.xr.setEnabled(Boolean(enabled && this.xr.isInXR));
    this.input.update(deltaSeconds);
    this.xr.update(deltaSeconds);
    if (phase === "practice_active" || phase === "scene_active") this.updateFocus();
    const now = performance.now();
    if (now - this.lastTimerUpdate > 250) { this.lastTimerUpdate = now; this.hud.updateTime(this.currentElapsedMs() / 1000); }
    const nearWall = this.maze.isHeadNearWall();
    this.hud.setVignette(this.moving, nearWall);
    this.maze.setMovementVignette(this.moving, nearWall);
    const sample = this.performanceMonitor.recordFrame(deltaMs);
    if (sample) {
      this.hud.updatePerformance(sample.fps, sample.tier);
      if (sample.tierChanged) { this.maze.applyPerformanceTier(sample.tier); this.log("performance_warning", { fps: sample.fps, tier: sample.tier }); }
    }
    this.maze.scene.render();
  }

  private updateFocus(): void {
    const raw = this.maze.getFocusedDirection();
    const now = performance.now();
    if (raw !== this.focusCandidate) {
      this.focusCandidate = raw;
      this.focusCandidateSince = now;
      if (raw === null) this.setFocus(null);
      return;
    }
    if (raw !== null && raw !== this.focusedDirection && now - this.focusCandidateSince >= FOCUS_DWELL_MS) this.setFocus(raw);
  }

  private setFocus(direction: Direction | null): void {
    if (this.focusedDirection === direction) return;
    this.focusedDirection = direction;
    this.maze.setFocused(direction);
    if (direction) this.log("direction_focused", { direction, dwellMs: FOCUS_DWELL_MS, decisionElapsedSeconds: this.elapsedSinceDecision() });
    this.hud.setFocus(direction, this.pendingDirection);
  }

  private previewAudio(): void {
    const phase = this.engine?.getPhase();
    if (phase !== "practice_active" && phase !== "scene_active" && phase !== "reveal") return;
    this.audio.preview();
    this.log("audio_preview", { source: "manual", audioId: this.requireEngine().getCurrentDecision()?.audio.id });
    this.hud.toastMessage("播放本次场景脚步声", "neutral");
  }

  private previewOrCancelSelection(): void {
    if (this.pendingDirection) this.cancelSelection();
    else this.previewAudio();
  }

  private primaryAction(): void {
    const engine = this.engine;
    if (!engine) return;
    if (engine.getPhase() === "reveal") {
      if (!this.revealReady) {
        this.hud.toastMessage("3D 场景仍在加载，请稍候。", "neutral");
        return;
      }
      this.completeReveal();
      return;
    }
    if ((engine.getPhase() !== "practice_active" && engine.getPhase() !== "scene_active") || !this.focusedDirection) {
      this.hud.toastMessage("请先注视一张场景照片。", "neutral");
      return;
    }
    if (this.pendingDirection !== this.focusedDirection) {
      this.pendingDirection = this.focusedDirection;
      this.selectedElapsedMs = this.currentElapsedMs();
      this.maze.setPending(this.pendingDirection);
      this.log("answer_selected", { direction: this.pendingDirection, decisionElapsedSeconds: this.elapsedSinceDecision() });
      this.hud.setFocus(this.focusedDirection, this.pendingDirection);
      return;
    }
    this.commitFinalAnswer(this.pendingDirection);
  }

  private commitFinalAnswer(direction: Direction): void {
    const engine = this.requireEngine();
    this.audio.stop();
    if (engine.getPhase() === "practice_active") {
      const outcome = engine.commitPractice(direction);
      this.log("practice_answer_committed", { direction, correct: outcome.correct, errors: outcome.errors });
      if (!outcome.correct) {
        this.pendingDirection = null;
        this.maze.setPending(null);
        this.hud.toastMessage("不匹配，请继续移动聆听后再试。", "wrong");
        return;
      }
      this.hud.toastMessage("练习正确，进入正式训练。", "correct");
      this.log("practice_complete", { errors: outcome.errors });
      this.startClock();
      this.sceneStartedElapsedMs = this.currentElapsedMs();
      this.startDecision(false);
      return;
    }

    const outcome = engine.commitFinalAnswer(direction);
    this.log("answer_committed", {
      direction,
      selectedSceneId: outcome.response.selectedSceneId,
      correct: outcome.response.correct,
      final: true,
      decisionElapsedSeconds: this.elapsedSinceDecision(),
      selectionToCommitSeconds: this.elapsedSinceSelection(),
    });
    if (outcome.response.correct) void this.startReveal(direction);
    else this.showSceneFailure();
  }

  private continueToNextDecision(): void {
    const engine = this.requireEngine();
    if (engine.getPhase() !== "scene_active") return;
    if (!this.pendingDirection) return void this.hud.toastMessage("请先选中一张照片，再决定是否继续下一组。", "neutral");
    if (engine.getDecisionIndex() + 1 >= engine.getDecisionCount()) {
      this.hud.toastMessage("已到本场景最后一次，请最终确认。", "neutral");
      return;
    }
    this.audio.stop();
    const outcome = engine.commitNonFinalAnswer(this.pendingDirection);
    this.log("answer_committed", {
      direction: this.pendingDirection,
      selectedSceneId: outcome.response.selectedSceneId,
      correct: outcome.response.correct,
      final: false,
      decisionElapsedSeconds: this.elapsedSinceDecision(),
      selectionToCommitSeconds: this.elapsedSinceSelection(),
    });
    this.hud.toastMessage(`已记录第 ${outcome.response.decisionIndex + 1} 次非最终判断`, "neutral");
    this.startDecision(false);
  }

  private async startReveal(direction: Direction): Promise<void> {
    const engine = this.requireEngine();
    const sceneId = engine.getCurrentDecision()?.targetScene.id ?? null;
    this.maze.showInstructionRoom(false);
    await this.enterXR();
    const decision = engine.getCurrentDecision();
    if (!decision) return;
    this.maze.configureSceneDecision(decision.directionColors, decision.candidates);
    this.maze.resetParticipant();
    this.audio.setStimulus(decision.audio);
    const target = decision.candidates.find((candidate) => candidate.isTarget);
    if (!target) throw new Error("揭示阶段缺少正确方向");
    this.maze.openDirection(direction);
    this.revealReady = false;
    this.pendingDirection = null;
    this.hud.hideModal();
    this.hud.setFocus(null, null, true);
    await this.maze.showSceneReveal(sceneId ?? "");
    this.revealReady = true;
    if (this.moving) this.audio.setMoving(true);
    this.log("reveal_started", { direction });
  }

  private completeReveal(): void {
    const engine = this.requireEngine();
    const completedScene = engine.getCurrentDecision()?.targetScene;
    const sceneElapsedMs = this.currentElapsedMs() - this.sceneStartedElapsedMs;
    this.log("scene_complete", { sceneId: completedScene?.id, effectiveElapsedSeconds: Math.round(sceneElapsedMs / 10) / 100 });
    engine.completeReveal();
    if (engine.getPhase() === "summary") {
      this.suspendClock();
      this.log("session_complete", { effectiveElapsedSeconds: this.accumulatedMs / 1000 });
      this.setStatus("complete");
      void this.xr.exit("summary");
      this.showSummary();
      return;
    }
    this.sceneStartedElapsedMs = this.currentElapsedMs();
    this.startDecision(false);
  }

  private showSceneFailure(): void {
    const engine = this.requireEngine();
    const failedScene = engine.getCurrentDecision()?.targetScene;
    const sceneElapsedMs = this.currentElapsedMs() - this.sceneStartedElapsedMs;
    const responses = engine.getResponses().filter((response) => response.sceneId === failedScene?.id);
    this.suspendClock();
    this.audio.stop();
    this.log("scene_failed", {
      sceneId: failedScene?.id,
      effectiveElapsedSeconds: Math.round(sceneElapsedMs / 10) / 100,
      responseCount: responses.length,
      finalDecisionIndex: engine.getDecisionIndex(),
    });
    void this.xr.exit("scene_failed").finally(() => {
      this.hud.showSceneFailure(engine.getSceneIndex() + 1, failedScene?.displayName ?? "未知场景", responses.length, () => this.completeFailedScene());
    });
  }

  private completeFailedScene(): void {
    const engine = this.requireEngine();
    engine.completeFailedScene();
    if (engine.getPhase() === "summary") {
      this.log("session_complete", { effectiveElapsedSeconds: this.accumulatedMs / 1000 });
      this.setStatus("complete");
      this.showSummary();
      return;
    }
    this.sceneStartedElapsedMs = this.currentElapsedMs();
    this.startClock();
    this.startDecision(false);
  }

  private cancelSelection(): void {
    if (!this.pendingDirection) return;
    this.log("answer_cancelled", { direction: this.pendingDirection });
    this.pendingDirection = null;
    this.selectedElapsedMs = null;
    this.maze.setPending(null);
    this.hud.setFocus(this.focusedDirection, null);
  }

  private onMovementChanged(moving: boolean): void {
    this.moving = moving;
    const phase = this.engine?.getPhase();
    const footstepPhase = phase === "practice_active" || phase === "scene_active" || phase === "reveal";
    this.audio.setMoving(moving && footstepPhase);
    if (moving && footstepPhase) this.log("footstep_audio_started", { trigger: "locomotion", phase });
  }

  private togglePause(): void {
    const engine = this.engine;
    if (!engine) return;
    if (engine.getPhase() === "paused") return void this.resumePaused();
    if (!["practice_active", "scene_active", "reveal"].includes(engine.getPhase())) return;
    engine.pause();
    this.suspendClock();
    this.audio.stop();
    this.log("pause_start", {});
    document.exitPointerLock();
    void this.xr.exit("pause");
    this.hud.showPause(() => void this.resumePaused(), () => this.endIncomplete());
  }

  private async resumePaused(): Promise<void> {
    const engine = this.requireEngine();
    await this.enterXR();
    engine.resume();
    this.resumeClockIfNeeded();
    this.log("pause_end", {});
    this.hud.hideModal();
  }

  private endIncomplete(): void {
    const engine = this.requireEngine();
    engine.endIncomplete();
    this.suspendClock();
    this.log("session_incomplete", { effectiveElapsedSeconds: this.accumulatedMs / 1000 });
    this.setStatus("incomplete");
    this.showSummary();
  }

  private onXRSessionEnded(reason: XRExitReason): void {
    this.moving = false;
    this.audio.setMoving(false);
    this.log("xr_exit", { reason });
    const phase = this.engine?.getPhase();
    if (reason === "user" && (phase === "practice_active" || phase === "scene_active" || phase === "reveal")) this.togglePause();
  }

  private async enterXR(): Promise<void> {
    if (!this.xrSupported) return;
    try { await this.xr.enter(); }
    catch (error) { this.xrSupported = false; this.hud.toastMessage(`VR 启动失败，改用桌面模式：${message(error)}`, "wrong"); }
  }

  private async resumeRecord(record: SceneSessionRecord): Promise<void> {
    this.audio.unlock();
    const catalog = await this.catalogRepository.load();
    this.record = record;
    this.engine = SceneTrainingEngine.fromSnapshot(catalog.scenes.filter((scene) => scene.enabled), record.engine);
    this.accumulatedMs = record.activeElapsedMs;
    this.sceneStartedElapsedMs = record.sceneStartedElapsedMs ?? record.activeElapsedMs;
    this.activeStartedAt = null;
    this.log("session_resumed", { interruptedTimeExcluded: true, restoredPhase: this.engine.getPhase() });
    const phase = this.engine.getPhase();
    if (phase === "instruction") return this.showInstruction();
    if (phase === "summary" || phase === "incomplete") return this.showSummary();
    if (phase === "paused") return this.hud.showPause(() => void this.resumePaused(), () => this.endIncomplete());
    if (phase === "scene_failed") {
      const failedScene = this.engine.getCurrentDecision()?.targetScene;
      const responses = this.engine.getResponses().filter((response) => response.sceneId === failedScene?.id).length;
      return this.hud.showSceneFailure(this.engine.getSceneIndex() + 1, failedScene?.displayName ?? "未知场景", responses, () => this.completeFailedScene());
    }
    await this.enterXR();
    this.resumeClockIfNeeded();
    if (phase === "reveal") {
      const decision = this.engine.getCurrentDecision();
      const target = decision?.candidates.find((candidate) => candidate.isTarget);
      if (decision && target) {
        this.maze.configureSceneDecision(decision.directionColors, decision.candidates);
        this.maze.openDirection(target.direction);
        this.audio.setStimulus(decision.audio);
        await this.maze.showSceneReveal(decision.targetScene.id);
        this.revealReady = true;
        this.hud.hideModal();
        this.hud.setFocus(null, null, true);
      }
    } else this.startDecision(phase === "practice_active");
  }

  private async abandon(record: SceneSessionRecord): Promise<void> {
    this.record = { ...record, status: "incomplete", updatedAt: new Date().toISOString() };
    await this.store.save(this.record);
    this.record = null;
    this.showPreflight();
  }

  private async showRecords(): Promise<void> {
    const records = await this.store.list();
    this.hud.showRecords(records, (id) => void this.downloadById(id), (id) => void this.removeRecord(id), () => this.showPreflight());
  }

  private async removeRecord(id: string): Promise<void> { await this.store.remove(id); await this.showRecords(); }
  private async downloadById(id: string): Promise<void> { const record = await this.store.get(id); if (record) await this.download(record); }
  private async download(record: SceneSessionRecord): Promise<void> { const result = await exportSceneEvents(record); this.hud.toastMessage(`日志已导出 · ${result.checksum.slice(0, 12)}…`, "correct"); }

  private showSummary(): void {
    const engine = this.requireEngine();
    const record = this.requireRecord();
    document.exitPointerLock();
    this.input.setEnabled(false);
    this.xr.setEnabled(false);
    this.hud.showSummary(engine.getResults(), this.currentElapsedMs() / 1000, record.events.length, () => void this.download(this.requireRecord()), () => window.location.reload());
  }

  private log(eventType: string, payload: Record<string, unknown>): void {
    const record = this.record;
    const engine = this.engine;
    if (!record || !engine) return;
    const decision = engine.getCurrentDecision();
    const base = { ...record, engine: engine.toSnapshot() };
    if (!BEHAVIOR_EVENT_TYPES.has(eventType)) {
      this.record = { ...base, updatedAt: new Date().toISOString() };
      void this.save();
      return;
    }
    const behaviorPayload = {
      phase: engine.getPhase(),
      sceneIndex: engine.getSceneIndex(),
      decisionIndex: decision?.decisionIndex ?? null,
      effectiveElapsedSeconds: this.roundSeconds(this.currentElapsedMs()),
      sceneElapsedSeconds: this.roundSeconds(this.currentElapsedMs() - this.sceneStartedElapsedMs),
      ...payload,
    };
    const event: SceneEvent = createSceneEvent(base, eventType, decision?.targetScene.id ?? null, decision?.decisionIndex ?? null, behaviorPayload);
    this.record = { ...base, events: [...record.events, event], updatedAt: new Date().toISOString() };
    void this.save();
  }

  private save(): Promise<void> {
    const record = this.record;
    const engine = this.engine;
    if (!record || !engine) return Promise.resolve();
    const nowElapsed = this.currentElapsedMs();
    if (this.activeStartedAt !== null) this.activeStartedAt = performance.now();
    this.accumulatedMs = nowElapsed;
    this.record = {
      ...record,
      engine: engine.toSnapshot(),
      updatedAt: new Date().toISOString(),
      activeElapsedMs: nowElapsed,
      sceneStartedElapsedMs: this.sceneStartedElapsedMs,
      activeClockStartedAt: null,
    };
    const snapshot = this.record;
    this.saveQueue = this.saveQueue.then(() => this.store.save(snapshot)).catch((error) => this.hud.toastMessage(`自动保存失败：${message(error)}`, "wrong"));
    return this.saveQueue;
  }

  private setStatus(status: SceneSessionRecord["status"]): void {
    if (!this.record) return;
    this.record = { ...this.record, status };
    void this.save();
  }

  private startClock(): void { if (this.activeStartedAt === null) this.activeStartedAt = performance.now(); }
  private suspendClock(): void {
    if (this.activeStartedAt === null) return;
    this.accumulatedMs += performance.now() - this.activeStartedAt;
    this.activeStartedAt = null;
  }
  private resumeClockIfNeeded(): void {
    const phase = this.engine?.getPhase();
    if (phase === "scene_active" || phase === "reveal") this.startClock();
  }
  private currentElapsedMs(): number { return this.accumulatedMs + (this.activeStartedAt === null ? 0 : performance.now() - this.activeStartedAt); }
  private elapsedSinceDecision(): number { return this.roundSeconds(this.currentElapsedMs() - this.decisionStartedElapsedMs); }
  private elapsedSinceSelection(): number | null { return this.selectedElapsedMs === null ? null : this.roundSeconds(this.currentElapsedMs() - this.selectedElapsedMs); }
  private roundSeconds(milliseconds: number): number { return Math.round(milliseconds / 10) / 100; }
  private requireEngine(): SceneTrainingEngine { if (!this.engine) throw new Error("Scene engine is not initialized"); return this.engine; }
  private requireRecord(): SceneSessionRecord { if (!this.record) throw new Error("Scene session is not initialized"); return this.record; }
}

function createSeed(): number { const values = new Uint32Array(1); crypto.getRandomValues(values); return values[0] ?? Date.now(); }
function message(error: unknown): string { return error instanceof Error ? error.message : String(error); }
